import React from 'react';
import SectionContainer from '../SectionContainer';
import HeroPanel from '../../../LandingPage/HeroPanel/HeroPanel';

/**
 * Helper function to extract content from field objects or return plain values
 * @param {Object|string} field - Field object with fieldType and content, or plain string
 * @returns {string} The content value
 */
const extractFieldContent = (field) => {
  if (!field) return undefined;
  // If it's an object with content property (CMS format), extract the content
  if (typeof field === 'object' && field.content !== undefined) {
    return field.content;
  }
  // Otherwise return as-is (plain string format)
  return field;
};

/**
 * Section component for the custom HeroPanel
 * This allows the HeroPanel to be used within the PageBuilder system
 * Handles both CMS field format ({ fieldType, content }) and plain string format
 *
 * @component
 * @param {Object} props
 * @param {string} props.sectionId - ID of the section
 * @param {string} [props.className] - Custom class name
 * @param {string} [props.rootClassName] - Root class name override
 * @param {string|Object} [props.title] - Main hero title (plain string or field object)
 * @param {string|Object} [props.description] - Description/subtitle text from CMS (plain string or field object)
 * @param {string|Object} [props.subtitle] - Subtitle text for backwards compatibility (plain string or field object)
 * @param {string|Object} [props.ctaText] - Call-to-action message (plain string or field object)
 * @param {string|Object} [props.ctaButtonText] - Button text (plain string or field object)
 * @param {string|Object} [props.ctaButtonLink] - Button link destination (plain string or field object)
 * @param {Object} [props.appearance] - Appearance configuration
 * @param {Object} [props.options] - Additional options
 * @returns {JSX.Element} Section containing HeroPanel
 */
const SectionHeroCustom = props => {
  const {
    sectionId,
    className,
    rootClassName,
    title,
    description,
    subtitle,
    ctaText,
    ctaButtonText,
    ctaButtonLink,
    appearance,
    options,
    // Extract React-specific props that don't need to be passed down
    defaultClasses,
    isInsideContainer,
    sectionType,
    sectionName,
    // Any remaining props that might be valid DOM attributes
    ...domProps
  } = props;

  // Extract content from field objects if they come from CMS
  const titleContent = extractFieldContent(title);
  // Use description from CMS if available, otherwise fall back to subtitle for backwards compatibility
  const subtitleContent = extractFieldContent(description) || extractFieldContent(subtitle);
  const ctaTextContent = extractFieldContent(ctaText);
  const ctaButtonTextContent = extractFieldContent(ctaButtonText);
  const ctaButtonLinkContent = extractFieldContent(ctaButtonLink);

  // Get isAuthenticated from options (passed through from PageBuilder)
  const isAuthenticated = options?.isAuthenticated;

  return (
    <SectionContainer
      id={sectionId}
      className={className}
      rootClassName={rootClassName}
      appearance={appearance}
      options={options}
      {...domProps}
    >
      <HeroPanel
        title={titleContent}
        subtitle={subtitleContent}
        ctaText={ctaTextContent}
        ctaButtonText={ctaButtonTextContent}
        ctaButtonLink={ctaButtonLinkContent}
        isAuthenticated={isAuthenticated}
      />
    </SectionContainer>
  );
};

export default SectionHeroCustom;

